import React from "react";
import { PageContainer } from "./PageContainer";
import { Section } from "./Section";
import { PageHeader } from "./PageHeader";
import { ToolsGridTemplate } from "./ToolsGridTemplate";
import { HeroBannerTemplate } from "./HeroBannerTemplate";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * CategoryTemplate
 *
 * Purpose
 * - Standard layout for category landing pages showing a primary content area with optional sidebar.
 * - Ensures consistent grid, spacing, and container styling across category experiences.
 *
 * Layout Rules (default / non-dense)
 * - Root (PageContainer): py-4 px-0 text-succession-text-primary overflow-x-hidden bg-succession-bg-secondary
 * - Inner grid: grid grid-cols-1 lg:grid-cols-12 gap-4 px-4
 * - Primary column: lg:col-span-9 space-y-4
 * - Sidebar (optional): lg:col-span-3 space-y-4
 * - In-section content grids: grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4
 *
 * Variants
 * - withSidebar (default): primary 9 / sidebar 3
 * - withoutSidebar: primary spans 12 (omit sidebar prop)
 * - dense: reduce gaps (gap-3), inter-section (space-y-3), card padding (p-3 md:p-4) handled by consumers
 */
export function CategoryTemplate({
  title,
  subtitle,
  actions,
  breadcrumbs,
  hero = null,             // optional: config for HeroBannerTemplate { title, blurb, ctaHref, ctaLabel, variant }
  sidebar = null,          // ReactNode | null
  dense = false,           // boolean
  variant = 'default',     // 'default' | 'wide' (constrain total width similar to SingleColumnTemplate)
  containerClassName = "", // extra classes for PageContainer
  headerClassName = "",    // extra classes for PageHeader
  gridClassName = "",      // extra classes for inner grid Section
  primaryClassName = "",   // extra classes for primary column
  sidebarClassName = "",   // extra classes for sidebar column
  tools = null,             // optional: array of tool objects for ToolsGridTemplate
  toolsTitle = "Popular Tools",
  toolsCardVariant = "borderless",
  renderToolItem = null,
  heroPosition = 'top',     // 'top' | 'bottom' — where to render the hero in primary column
  children,                // primary content
  ...props
}) {
  const gap = dense ? "gap-3" : "gap-4";
  const spaceY = dense ? "space-y-3" : "space-y-4";

  const rootTokens = "text-succession-text-primary overflow-x-hidden bg-succession-bg-secondary";
  const containerMaxWidth = variant === 'wide' ? '66rem' : '55rem';

  return (
    <PageContainer
      className={cx("py-4 px-0", rootTokens, containerClassName)}
      {...props}
    >
      <div className={cx("px-4 flex justify-center")}> 
        <div style={{ maxWidth: containerMaxWidth, width: '100%' }}>
          {/* When hero is at bottom, render header above the grid so both columns align at the top */}
          {heroPosition === 'bottom' && (title || subtitle || actions) ? (
            <PageHeader title={title} subtitle={subtitle} actions={actions} className={headerClassName} />
          ) : null}
          <Section className={cx("grid grid-cols-1 lg:grid-cols-12 items-start", gap, gridClassName)}>
        <div
          className={cx(
            sidebar ? "lg:col-span-9" : "lg:col-span-12",
            spaceY,
            primaryClassName
          )}
        >
          {/* Optional hero banner at configurable position within the primary column */}
          {hero && heroPosition === 'top' ? (
            <HeroBannerTemplate
              title={hero.title}
              blurb={hero.blurb}
              ctaHref={hero.ctaHref}
              ctaLabel={hero.ctaLabel}
              variant={hero.variant}
              icon={hero.icon}
            />
          ) : null}
          {/* Header for top-hero case is handled above the grid when heroPosition === 'bottom' */}
          {children}
          {hero && heroPosition === 'bottom' ? (
            <HeroBannerTemplate
              title={hero.title}
              blurb={hero.blurb}
              ctaHref={hero.ctaHref}
              ctaLabel={hero.ctaLabel}
              variant={hero.variant}
              icon={hero.icon}
            />
          ) : null}
        </div>

        {sidebar ? (
          <aside className={cx("lg:col-span-3", spaceY, sidebarClassName)}>
            {sidebar}
          </aside>
        ) : tools && Array.isArray(tools) && tools.length > 0 ? (
          <aside className={cx("lg:col-span-3", spaceY, sidebarClassName)}>
            <ToolsGridTemplate
              title={toolsTitle}
              tools={tools}
              cardVariant={toolsCardVariant}
              renderItem={renderToolItem}
            />
          </aside>
        ) : null}
          </Section>
        </div>
      </div>
    </PageContainer>
  );
}


